import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/format";
import { inputCls, labelCls } from "@/components/AuthShell";
import { PageLoader, EmptyState } from "@/components/common";
import { ArrowLeft, LifeBuoy, Plus, Mail } from "lucide-react";

export default function Support() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const { data: tickets, isLoading } = useQuery({ queryKey: ["support-mine"], queryFn: () => apiFetch("/support/mine"), enabled: !!user });

  const submit = async () => {
    try {
      await apiFetch("/support", { method: "POST", body: JSON.stringify({ subject: subject.trim(), message: message.trim() }) });
      toast.success("Message sent to the Venda Team");
      setShowForm(false); setSubject(""); setMessage("");
      qc.invalidateQueries({ queryKey: ["support-mine"] });
    } catch (err) { toast.error(err.message); }
  };

  const closeTicket = async (id) => {
    try { await apiFetch(`/support/${id}/close`, { method: "POST" }); qc.invalidateQueries({ queryKey: ["support-mine"] }); }
    catch (err) { toast.error(err.message); }
  };

  if (user && isLoading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1.5 hover:bg-secondary"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="font-display text-xl font-extrabold">Support</h1>
        </div>
        {user && (
          <button onClick={() => setShowForm((s) => !s)} className="flex items-center gap-1.5 rounded-full bg-price px-4 py-2 text-sm font-semibold text-white" data-testid="new-ticket-btn"><Plus className="h-4 w-4" />New</button>
        )}
      </div>

      {/* Always-visible contact method (publicly reachable) */}
      <div className="mt-5 rounded-2xl border border-border bg-secondary/40 p-4" data-testid="support-contact">
        <p className="text-sm text-muted-foreground">Need help with Venda Market? Our team is here for you.</p>
        <a href="mailto:support@vendamarket.com" className="mt-3 flex items-center gap-2 text-sm font-semibold text-price" data-testid="support-email">
          <Mail className="h-4 w-4" /> support@vendamarket.com
        </a>
        <p className="mt-2 text-xs text-muted-foreground">We usually reply within 1–2 business days.</p>
      </div>

      {!user && (
        <p className="mt-6 text-sm text-muted-foreground">
          Sign in to open a support ticket and track its status, or email us directly using the address above.
        </p>
      )}

      {user && (
        <>
          {showForm && (
        <div className="mt-4 space-y-3 rounded-2xl border border-border p-4">
          <div><label className={labelCls}>Subject</label><input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="ticket-subject" /></div>
          <div><label className={labelCls}>Message</label><textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className={inputCls} data-testid="ticket-message" /></div>
          <button onClick={submit} disabled={message.trim().length < 3} className="rounded-xl bg-price px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="send-ticket">Send</button>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {(tickets || []).length === 0 ? (
          <EmptyState icon={LifeBuoy} title="No support requests" message="Need help? Start a conversation with our team." />
        ) : (
          tickets.map((t) => (
            <div key={t.id} className="rounded-2xl border border-border p-4" data-testid={`ticket-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{t.subject}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${t.status === "closed" ? "bg-gray-100 text-gray-500" : t.status === "answered" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{t.status}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{t.message}</p>
              {t.replies?.map((r, i) => (
                <div key={i} className="mt-2 rounded-xl bg-secondary p-3 text-sm">
                  <p className="text-xs font-semibold text-brand">Venda Team · {timeAgo(r.at)}</p>
                  <p className="mt-0.5">{r.message}</p>
                </div>
              ))}
              {t.status !== "closed" && (
                <button onClick={() => closeTicket(t.id)} className="mt-3 text-xs font-medium text-muted-foreground hover:text-destructive" data-testid={`close-ticket-${t.id}`}>Mark as resolved</button>
              )}
            </div>
          ))
        )}
      </div>
        </>
      )}
    </div>
  );
}
